import { useState, useMemo } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import ChurchCard from './components/ChurchCard';
import ChurchTable from './components/ChurchTable';
import FilterBar from './components/FilterBar';
import { churches } from '../../mocks/churches';

export default function Directory() {
  const [selectedRegion, setSelectedRegion] = useState('All');
  const [selectedState, setSelectedState] = useState('All');
  const [selectedUnion, setSelectedUnion] = useState('All');
  const [selectedConference, setSelectedConference] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationError, setLocationError] = useState('');
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [showNearbyOnly, setShowNearbyOnly] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const itemsPerPage = 12;

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Get user's current location
  const findNearbyChurches = () => {
    setIsLoadingLocation(true);
    setLocationError('');

    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      setIsLoadingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setShowNearbyOnly(true);
        setIsLoadingLocation(false);
        setCurrentPage(1);
      },
      (error) => {
        let errorMessage = 'Unable to retrieve your location';
        if (error.code === error.PERMISSION_DENIED) {
          errorMessage = 'Location access denied. Please enable location services.';
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          errorMessage = 'Location information unavailable';
        } else if (error.code === error.TIMEOUT) {
          errorMessage = 'Location request timed out';
        }
        setLocationError(errorMessage);
        setIsLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  // Enhanced search with fuzzy matching across all relevant fields
  const filteredChurches = useMemo(() => {
    let results = churches.filter(church => {
      // Search is ALWAYS active and superior - matches across all fields
      const searchLower = searchQuery.toLowerCase().trim();
      const matchesSearch = searchQuery === '' || 
        church.name.toLowerCase().includes(searchLower) ||
        church.location.toLowerCase().includes(searchLower) ||
        church.state.toLowerCase().includes(searchLower) ||
        church.region.toLowerCase().includes(searchLower) ||
        church.union.toLowerCase().includes(searchLower) ||
        church.conference.toLowerCase().includes(searchLower) ||
        'sda'.includes(searchLower) ||
        'seventh-day adventist'.includes(searchLower) ||
        'adventist'.includes(searchLower);

      // Dropdown filters only refine search results, never block them
      const matchesRegion = selectedRegion === 'All' || church.region === selectedRegion;
      const matchesState = selectedState === 'All' || church.state === selectedState;
      const matchesUnion = selectedUnion === 'All' || church.union === selectedUnion;
      const matchesConference = selectedConference === 'All' || church.conference === selectedConference;

      return matchesSearch && matchesRegion && matchesState && matchesUnion && matchesConference;
    });

    // Add distance to each church if user location is available
    if (userLocation) {
      results = results.map(church => ({
        ...church,
        distance: calculateDistance(
          userLocation.lat,
          userLocation.lng,
          church.coordinates.lat,
          church.coordinates.lng
        ),
      }));

      // Sort by distance
      results.sort((a, b) => (a.distance || 0) - (b.distance || 0));

      // If showing nearby only, filter to churches within 50km
      if (showNearbyOnly) {
        results = results.filter(church => (church.distance || 0) <= 50);
      }
    }

    return results;
  }, [churches, searchQuery, selectedRegion, selectedState, selectedUnion, selectedConference, userLocation, showNearbyOnly]);

  // Pagination
  const totalPages = Math.ceil(filteredChurches.length / itemsPerPage);
  const paginatedChurches = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredChurches.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredChurches, currentPage]);

  // Generate smart suggestions based on search query
  const suggestions = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return null;

    const searchLower = searchQuery.toLowerCase();
    const churchMatches = churches.filter(c => 
      c.name.toLowerCase().includes(searchLower)
    ).slice(0, 3);

    const cityMatches = [...new Set(churches
      .filter(c => c.location.toLowerCase().includes(searchLower))
      .map(c => c.location)
    )].slice(0, 3);

    const stateMatches = [...new Set(churches
      .filter(c => c.state.toLowerCase().includes(searchLower))
      .map(c => c.state)
    )].slice(0, 3);

    const conferenceMatches = [...new Set(churches
      .filter(c => c.conference.toLowerCase().includes(searchLower))
      .map(c => c.conference)
    )].slice(0, 3);

    if (churchMatches.length === 0 && cityMatches.length === 0 && 
        stateMatches.length === 0 && conferenceMatches.length === 0) {
      return null;
    }

    return { churchMatches, cityMatches, stateMatches, conferenceMatches };
  }, [searchQuery, churches]);

  const clearAllFilters = () => {
    setSelectedRegion('All');
    setSelectedState('All');
    setSelectedUnion('All');
    setSelectedConference('All');
    setShowNearbyOnly(false);
    setUserLocation(null);
    setCurrentPage(1);
  };

  const hasActiveFilters = selectedRegion !== 'All' || selectedState !== 'All' || 
                          selectedUnion !== 'All' || selectedConference !== 'All' || showNearbyOnly;

  // Reset to page 1 when filters change
  const handleFilterChange = (setter: (value: string) => void, value: string) => {
    setter(value);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 text-white">
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
              Church Directory
            </h1>
            <p className="text-lg sm:text-xl text-white/95 max-w-3xl mx-auto mb-8 sm:mb-12">
              Discover and connect with Seventh-day Adventist churches across Nigeria
            </p>
            
            {/* Enhanced Search Bar with Nearby Button */}
            <div className="max-w-2xl mx-auto relative">
              <div className="flex gap-3 mb-3">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    placeholder="Search by church name, city, state, conference, or union..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                      setShowSuggestions(true);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    className="w-full px-5 py-4 pl-12 rounded-xl text-gray-900 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-xl"
                  />
                  <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
                  {searchQuery && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setCurrentPage(1);
                        setShowSuggestions(false);
                      }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className="ri-close-line text-xl"></i>
                    </button>
                  )}
                </div>
                
                <button
                  onClick={findNearbyChurches}
                  disabled={isLoadingLocation}
                  className="px-6 py-4 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors shadow-xl whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isLoadingLocation ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span className="hidden sm:inline">Finding...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-map-pin-user-line text-xl"></i>
                      <span className="hidden sm:inline">SDA Churches Near Me</span>
                      <span className="sm:hidden">Near Me</span>
                    </>
                  )}
                </button>
              </div>

              {/* Location Status Messages */}
              {locationError && (
                <div className="bg-rose-500/90 text-white px-4 py-3 rounded-lg text-sm mb-3 flex items-center gap-2">
                  <i className="ri-error-warning-line"></i>
                  <span>{locationError}</span>
                  <button
                    onClick={() => setLocationError('')}
                    className="ml-auto cursor-pointer"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              )}

              {userLocation && showNearbyOnly && (
                <div className="bg-emerald-500/90 text-white px-4 py-3 rounded-lg text-sm mb-3 flex items-center gap-2">
                  <i className="ri-map-pin-line"></i>
                  <span>Showing churches within 50km of your location</span>
                  <button
                    onClick={() => {
                      setShowNearbyOnly(false);
                      setUserLocation(null);
                      setCurrentPage(1);
                    }}
                    className="ml-auto cursor-pointer hover:bg-white/20 px-2 py-1 rounded"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              )}

              {/* Smart Suggestions Dropdown */}
              {suggestions && !showNearbyOnly && showSuggestions && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl overflow-hidden z-50 text-left">
                  {suggestions.churchMatches.length > 0 && (
                    <div className="p-3 border-b border-gray-100">
                      <div className="text-xs font-semibold text-gray-500 mb-2 px-2">CHURCHES</div>
                      {suggestions.churchMatches.map((church, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setSearchQuery(church.name);
                            setCurrentPage(1);
                            setShowSuggestions(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg text-sm text-gray-900 cursor-pointer"
                        >
                          <i className="ri-building-line text-emerald-600 mr-2"></i>
                          {church.name}
                        </button>
                      ))}
                    </div>
                  )}

                  {suggestions.cityMatches.length > 0 && (
                    <div className="p-3 border-b border-gray-100">
                      <div className="text-xs font-semibold text-gray-500 mb-2 px-2">CITIES</div>
                      {suggestions.cityMatches.map((city, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setSearchQuery(city);
                            setCurrentPage(1);
                            setShowSuggestions(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg text-sm text-gray-900 cursor-pointer"
                        >
                          <i className="ri-map-pin-line text-emerald-600 mr-2"></i>
                          {city}
                        </button>
                      ))}
                    </div>
                  )}

                  {suggestions.stateMatches.length > 0 && (
                    <div className="p-3 border-b border-gray-100">
                      <div className="text-xs font-semibold text-gray-500 mb-2 px-2">STATES</div>
                      {suggestions.stateMatches.map((state, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setSearchQuery(state);
                            setCurrentPage(1);
                            setShowSuggestions(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg text-sm text-gray-900 cursor-pointer"
                        >
                          <i className="ri-map-2-line text-emerald-600 mr-2"></i>
                          {state}
                        </button>
                      ))}
                    </div>
                  )}

                  {suggestions.conferenceMatches.length > 0 && (
                    <div className="p-3">
                      <div className="text-xs font-semibold text-gray-500 mb-2 px-2">CONFERENCES</div>
                      {suggestions.conferenceMatches.map((conference, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setSearchQuery(conference);
                            setCurrentPage(1);
                            setShowSuggestions(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg text-sm text-gray-900 cursor-pointer"
                        >
                          <i className="ri-organization-chart text-emerald-600 mr-2"></i>
                          {conference}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Results */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <FilterBar
          selectedRegion={selectedRegion}
          setSelectedRegion={(value) => handleFilterChange(setSelectedRegion, value)}
          selectedState={selectedState}
          setSelectedState={(value) => handleFilterChange(setSelectedState, value)}
          selectedUnion={selectedUnion}
          setSelectedUnion={(value) => handleFilterChange(setSelectedUnion, value)}
          selectedConference={selectedConference}
          setSelectedConference={(value) => handleFilterChange(setSelectedConference, value)}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />

        <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
          <p className="text-sm text-gray-600">
            Showing <strong>{filteredChurches.length}</strong> of <strong>{churches.length}</strong> churches
            {userLocation && showNearbyOnly && <span className="text-emerald-600 font-semibold"> near you</span>}
          </p>
          
          <div className="flex items-center gap-3">
            {/* View Toggle */}
            <div className="flex items-center bg-white rounded-lg shadow-sm border border-gray-200 p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  viewMode === 'grid'
                    ? 'bg-emerald-600 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-grid-line mr-2"></i>
                Grid
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  viewMode === 'table'
                    ? 'bg-emerald-600 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-list-check mr-2"></i>
                Table
              </button>
            </div>

            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-filter-off-line mr-2"></i>
                Clear All Filters
              </button>
            )}
          </div>
        </div>

        {/* No Results State */}
        {filteredChurches.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl shadow-md">
            <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No churches found</h3>
            <p className="text-gray-600 mb-6">
              {showNearbyOnly 
                ? 'No churches found within 50km of your location. Try expanding your search.'
                : 'Try adjusting your search or filters to find what you\'re looking for'}
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setCurrentPage(1);
                  }}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-close-line mr-2"></i>
                  Clear Search
                </button>
              )}
              {showNearbyOnly && (
                <button
                  onClick={() => {
                    setShowNearbyOnly(false);
                    setUserLocation(null);
                    setCurrentPage(1);
                  }}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Show All Churches
                </button>
              )}
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Reset All Filters
                </button>
              )}
            </div>
          </div>
        )}

        {/* Results Grid or Table */}
        {filteredChurches.length > 0 && (
          <>
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedChurches.map(church => (
                  <ChurchCard key={church.id} church={church} />
                ))}
              </div>
            ) : (
              <ChurchTable churches={paginatedChurches} />
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-s-line mr-1"></i>
                  Previous
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                    // Show first page, last page, current page, and pages around current
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap ${
                            currentPage === page
                              ? 'bg-emerald-600 text-white'
                              : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (
                      page === currentPage - 2 ||
                      page === currentPage + 2
                    ) {
                      return <span key={page} className="px-2 text-gray-400">...</span>;
                    }
                    return null;
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  Next
                  <i className="ri-arrow-right-s-line ml-1"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}
